import { useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, ShieldCheck, Megaphone, Truck, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { IMG } from "@/lib/mockData";
import { useSite } from "@/context/SiteContext";

const BENEFITS = [
  { icon: TrendingUp, title: "Grow your revenue", text: "Reach thousands of eaters hunting for authentic food — without building tech yourself." },
  { icon: ShieldCheck, title: "Verified badge", text: "Our verification builds instant trust and higher conversion for your kitchen." },
  { icon: Megaphone, title: "Built-in marketing", text: "Featured slots, promos and the discovery hub put your dishes in front of the right people." },
  { icon: Truck, title: "Logistics handled", text: "Delivery, payments and support run on us. You focus on cooking." },
];

const DEFAULT_TIERS = [
  { name: "Indie Chef", price: "0%", sub: "for first 3 months", features: ["Personal chef profile", "Up to 20 dishes", "Standard payouts", "Community support"], accent: false },
  { name: "Kitchen Pro", price: "12%", sub: "per order", features: ["Everything in Indie", "Featured placements", "Priority verification", "Analytics dashboard", "Dedicated manager"], accent: true },
  { name: "Restaurant", price: "Custom", sub: "let's talk", features: ["Multi-location support", "Team accounts", "API & POS integration", "Custom contracts"], accent: false },
];

export default function Partnership() {
  const [form, setForm] = useState({ name: "", business: "", email: "", message: "" });
  const { config, t: tr } = useSite();
  const TIERS = config?.pricing?.tiers?.length ? config.pricing.tiers : DEFAULT_TIERS;

  const submit = (e) => {
    e.preventDefault();
    toast.success("Application received! Our partnerships team will reach out within 48 hours.");
    setForm({ name: "", business: "", email: "", message: "" });
  };

  return (
    <div data-testid="partnership-page">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center grain">
        <img src={IMG.restaurant1} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.3em] text-[#f5a623]">Partnerships & business</span>
            <h1 className="font-display text-5xl sm:text-7xl font-black tracking-tighter text-white leading-[0.95] mt-4">
              Turn your recipes into a real business.
            </h1>
            <p className="mt-6 text-lg text-neutral-200 max-w-xl">
              Whether you're a home chef with a cult following or a restaurant ready to scale,
              Jollanda gives you the platform, audience and tools to thrive.
            </p>
            <a href="#apply"><Button className="mt-8 bg-[#d00000] hover:bg-[#a30000] text-white rounded-none h-12 px-8" data-testid="hero-apply-btn">Apply to partner</Button></a>
          </motion.div>
        </div>
      </section>

      {/* Stats band */}
      <section className="border-y border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[["200+", "Verified chefs"], ["€3.8M", "Paid to partners"], ["40+", "Cuisines"], ["4.9★", "Avg chef rating"]].map(([v, l]) => (
            <div key={l}>
              <p className="font-display text-4xl sm:text-5xl font-black text-neutral-900">{v}</p>
              <p className="text-sm text-neutral-500 mt-2">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-3">Why Jollanda</p>
        <h2 className="font-display text-4xl sm:text-5xl font-black text-neutral-900 mb-12">Built for indie kitchens.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {BENEFITS.map((b, i) => (
            <motion.div key={b.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="bg-white border border-neutral-200 p-8" data-testid={`benefit-${i}`}>
              <b.icon className="w-8 h-8 text-[#d00000] mb-5" />
              <h3 className="font-display text-xl font-bold text-neutral-900">{b.title}</h3>
              <p className="text-neutral-600 mt-3 leading-relaxed">{b.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-3">Simple, fair pricing</p>
        <h2 className="font-display text-4xl sm:text-5xl font-black text-neutral-900 mb-12">Choose how you grow.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TIERS.map((t, i) => (
            <motion.div key={t.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className={`p-8 border ${t.accent ? "border-[#d00000] bg-[#d00000]/5" : "border-neutral-200 bg-white"}`} data-testid={`tier-${t.name.toLowerCase().replace(/\s/g, "-")}`}>
              {t.accent && <span className="text-xs uppercase tracking-widest text-[#d00000]">{tr("Most popular")}</span>}
              <h3 className="font-display text-2xl font-bold text-neutral-900 mt-2">{tr(t.name)}</h3>
              <p className="font-display text-5xl font-black text-neutral-900 mt-4">{t.price}</p>
              <p className="text-sm text-neutral-500">{tr(t.sub)}</p>
              <ul className="mt-6 space-y-3">
                {(t.features || []).map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-neutral-700"><Check className="w-4 h-4 text-[#d00000]" /> {tr(f)}</li>
                ))}
              </ul>
              <a href="#apply"><Button className={`w-full mt-8 rounded-none ${t.accent ? "bg-[#d00000] hover:bg-[#a30000] text-white" : "bg-transparent border border-neutral-300 text-neutral-900 hover:bg-neutral-100"}`}>Get started</Button></a>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Apply form */}
      <section id="apply" className="border-t border-neutral-200 bg-white py-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-3">Let's cook together</p>
          <h2 className="font-display text-4xl sm:text-5xl font-black text-neutral-900 mb-8">Apply to partner.</h2>
          <form onSubmit={submit} className="space-y-5" data-testid="partner-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="partner-name" className="h-12 bg-[#fbf9f7] border-neutral-300 text-neutral-900 rounded-none focus-visible:ring-[#d00000]" />
              <Input required value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} placeholder="Kitchen / restaurant name" data-testid="partner-business" className="h-12 bg-[#fbf9f7] border-neutral-300 text-neutral-900 rounded-none focus-visible:ring-[#d00000]" />
            </div>
            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="partner-email" className="h-12 bg-[#fbf9f7] border-neutral-300 text-neutral-900 rounded-none focus-visible:ring-[#d00000]" />
            <Textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your cuisine and why you'd be a great fit…" rows={5} data-testid="partner-message" className="bg-[#fbf9f7] border-neutral-300 text-neutral-900 rounded-none focus-visible:ring-[#d00000]" />
            <Button type="submit" className="bg-[#d00000] hover:bg-[#a30000] text-white rounded-none h-12 px-8" data-testid="partner-submit">Submit application</Button>
          </form>
        </div>
      </section>
    </div>
  );
}
